import { Button } from '@components/Button/Button';
import { Modal } from '@components/common/Modal/Modal';
import { App, Checkbox, Spin } from 'antd';
import { useState } from 'react';

import { useGetDictionariesQuery } from '@/api/dictionaries/api';
import {
  useApproveEventProtocolMutation,
  useUpdateTaskIndividualsMutation,
} from '@/api/vaccination/task/api';
import { TabletFarmIndividual } from '@/pages/TaskTablet/components/farmIndividual/TabletFarmIndividual';
import { getExecutorGeolocation } from '@/pages/TaskTablet/components/getExecutorGeolocation';
import {
  EventProtocol,
  TaskIndividualPayload,
} from '@/pages/Vaccination/components/task/types';
import { VACCINATION_STATUS } from '@/shared/vaccination/enums';

import cl from './styles.module.scss';

interface FarmEventItem extends EventProtocol {
  isTransformData: boolean;
  duplicateInventoryNumbers: Set<string>;
  onInventoryClick?: (value: string) => void;
}

export const FarmEventItem = ({
  id,
  drugId,
  administrationRouteIds,
  researchTypeId,
  animalSpeciesId,
  individuals,
  isVaccinationApproved,
  isTransformData,
  duplicateInventoryNumbers,
  onInventoryClick,
}: FarmEventItem) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const { message } = App.useApp();
  const { data: dictionaries } = useGetDictionariesQuery();
  const [eventCompleted, setEventCompleted] = useState(isVaccinationApproved);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const [triggerApproveProtocol, { isLoading: isLoadingApprove }] =
    useApproveEventProtocolMutation();
  const [triggerUpdateIndividuals, { isLoading: indsLoading }] =
    useUpdateTaskIndividualsMutation();

  const handleApproveEvent = async () => {
    await triggerApproveProtocol({ id }).unwrap();
    message.success('Данные успешно сохранены');
  };

  const handleEventCheckbox = async () => {
    if (isVaccinationApproved) return;

    try {
      setIsGettingLocation(true);

      const geolocation = await getExecutorGeolocation((text) => message.warning(text));

      const prepareIndivids = individuals
        .map((item) => {
          if (!eventCompleted) {
            if (item.status !== VACCINATION_STATUS.RESERVE) {
              return {
                isVaccinated: true,
                cancelReason: null,
                id: item.id,
                ...geolocation,
              };
            } else {
              return {
                isVaccinated: null,
                id: item.id,
                cancelReason: null,
                ...geolocation,
              };
            }
          } else {
            return {
              isVaccinated: null,
              id: item.id,
              cancelReason: null,
              ...geolocation,
            };
          }
        })
        .filter(Boolean) as TaskIndividualPayload[];

      await triggerUpdateIndividuals(prepareIndivids).unwrap();
      setEventCompleted(!eventCompleted);
      message.success('Данные успешно сохранены');
    } catch (e: any) {
      message.error(e.data?.message || 'Ошибка при сохранении');
    } finally {
      setIsGettingLocation(false);
    }
  };

  const isLoading = indsLoading || isLoadingApprove || isGettingLocation;

  return (
    <>
      <div
        className={`${cl.wrap} ${isLoading ? cl.loading : ''} ${isVaccinationApproved ? 'event-item-completed' : ''}`}>
        <div className={cl.header}>
          <Checkbox
            className={cl.checkbox}
            onChange={handleEventCheckbox}
            checked={eventCompleted}
            disabled={isLoading || isVaccinationApproved}
          />
          <div className={cl.descr}>
            <span>
              Мероприятие:{' '}
              <b>{dictionaries?.dictMap?.RESEARCH_TYPE[researchTypeId]?.name || ''}</b>
            </span>
            <span>
              Препарат: <b>{dictionaries?.dictMap?.DRUG[drugId]?.name || ''}</b>
            </span>
            <span>
              Способ введения:{' '}
              <b>
                {administrationRouteIds
                  ?.map(
                    (value: string) =>
                      dictionaries?.dictMap?.ADMINISTRATION_ROUTE[value]?.name || '',
                  )
                  ?.join(', ') || ''}
              </b>
            </span>
            {animalSpeciesId && (
              <span>
                Группа:{' '}
                <b>
                  {dictionaries?.dictMap?.ANIMAL_SPECIES[animalSpeciesId]?.name || ''}
                </b>
              </span>
            )}
          </div>
        </div>
        <div className={cl.individualWrap}>
          {individuals.map((item) => {
            const isReserveDisabled =
              item.status === VACCINATION_STATUS.RESERVE &&
              !individuals.some(
                (ind) =>
                  ind.orderNumber === item.orderNumber &&
                  ind.status === VACCINATION_STATUS.WAITING_LIST &&
                  ind.isVaccinated === false,
              );

            return (
              <TabletFarmIndividual
                key={item.id}
                {...item}
                isEventApproved={isVaccinationApproved}
                isDisabled={isReserveDisabled || isLoading}
                isDuplicate={duplicateInventoryNumbers.has(item.inventoryNumber)}
                onInventoryClick={onInventoryClick}
              />
            );
          })}
        </div>
        {!isTransformData && (
          <Button
            className={cl.submit}
            onClick={() => setIsConfirmOpen(true)}
            text="Подтвердить"
            loading={isLoadingApprove}
            type="primary"
            disabled={isVaccinationApproved || isLoading}
          />
        )}
        {isLoading && <Spin className={cl.spin} />}
      </div>
      <Modal
        mode={'confirm'}
        open={isConfirmOpen}
        onCancel={() => {
          setIsConfirmOpen(false);
        }}
        onSubmit={() => {
          setIsConfirmOpen(false);
          handleApproveEvent();
        }}
        text="Вы уверены, что хотите подтвердить выполнение?"
        submitText={'Да'}
        cancelText={'Нет'}
        destroyOnHidden={true}
      />
    </>
  );
};
